/**
 * @file Patient detail field renderer.
 */

import { appData, appDataDetails, state } from '../../app/state/state.js';
import { canAccessView } from '../../app/routing/boardAccess.js';
import {
    formatCurrency,
    isColorDark,
} from "../../shared/utils/displayUtils.js";
import {
    formatDate,
} from "../../shared/utils/dateUtils.js";
import {
    getResolvedCardDate,
} from "../../shared/board/itemSortUtils.js";
import {
    getNextActionDate,
} from "../../shared/utils/itemLookup.js";
import {
    convertIdsToNamesForEditing,
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    renderAuditTrail,
    renderLongTextDetailSection,
} from '../../shared/details/detailSections.js';
import {
    createDateControl,
} from '../../shared/ui/controls/dateControl.js';
import {
    createPrioritySelectPreset,
    createProviderSelectPreset,
    createStatusSelectPreset,
    createTypeSelectPreset,
    createUserSelectPreset,
} from '../../shared/ui/controls/controlPresets.js';
import {
    createSelectControl,
} from '../../shared/ui/controls/selectControl.js';
import {
    DETAIL_TITLE_INPUT_CLASSES,
    NATIVE_INPUT_CLASSES,
    getNativeFieldWrapperHtml,
    getReadOnlyValueHtml,
} from '../../shared/forms/nativeFieldStyles.js';
import {
    getUserDisplayNameById,
} from '../../shared/utils/userDisplay.js';
import {
    isItemClosed,
} from "../../shared/utils/closedState.js";

const CLOSED_PATIENT_LABS_SUMMARY_BUTTON_CLASSES = [
    'closed-patient-labs-summary',
    'mt-2',
    'flex',
    'min-h-8',
    'w-full',
    'items-center',
    'gap-1.5',
    'text-left',
    'text-sm',
    'transition-colors',
    'focus:outline-none',
    'focus:text-[var(--dfc-brand-primary)]',
].join(' ');

const PATIENT_LABS_UI_SESSION = {
    patientId: null,
    closedExpanded: false,
};

function attrValue(value = '') {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function inputHtml(type, name, value = '', options = {}) {
    const placeholderAttr = options.placeholder ? ` placeholder="${attrValue(options.placeholder)}"` : '';
    const requiredAttr = options.required ? ' required' : '';
    const extraAttrs = options.extraAttrs ? ` ${options.extraAttrs}` : '';
    const className = [NATIVE_INPUT_CLASSES, options.className || '']
        .filter(Boolean)
        .join(' ');
    return `<input type="${type}" name="${name}" value="${attrValue(value)}" class="${className}"${placeholderAttr}${requiredAttr}${extraAttrs}>`;
}

function scalarControlRootHtml(fieldName) {
    return `<div class="min-w-0" data-detail-scalar-control="${fieldName}"></div>`;
}

function canAccessLabs() {
    return canAccessView(state.currentUserProfile, 'labs');
}

function getPaymentMethodOptions(selectedValue = '') {
    const methods = Array.isArray(appData?.config?.paymentMethods)
        ? appData.config.paymentMethods
        : [];
    const options = methods.map((method) => {
        if (method && typeof method === 'object') {
            const value = String(method.id ?? method.value ?? method.name ?? '');
            return {
                value,
                label: String(method.name ?? method.label ?? value),
                order: Number(method.order ?? 9999),
            };
        }
        return {
            value: String(method ?? ''),
            label: String(method ?? ''),
            order: 9999,
        };
    }).filter((option) => option.value);

    if (selectedValue && !options.some((option) => option.value === selectedValue)) {
        options.push({
            value: selectedValue,
            label: selectedValue,
            selectedOnly: true,
            unavailableLabel: 'saved',
            order: 10000,
        });
    }

    return options.sort((optionA, optionB) => {
        const orderDiff = optionA.order - optionB.order;
        return orderDiff || optionA.label.localeCompare(optionB.label);
    });
}

/**
 * Generates HTML for editable Patient detail fields.
 * @param {object} item Patient item data.
 * @returns {string} Patient detail fields HTML.
 */
export function getPatientDetailFields(item) {
    const val = (field, fallback = '') => item?.[field] ?? fallback;

    const nextActionDate = getNextActionDate(item.id, appDataDetails.tasks);
    return `
        <div class="bg-white pt-6 px-6 pb-3 rounded-lg shadow-md mb-6">
            <div class="mb-4 pb-2">
                <textarea name="name" class="${DETAIL_TITLE_INPUT_CLASSES} resize-none overflow-hidden" placeholder="Patient Name" aria-label="Patient Name" autocomplete="off" rows="1" data-auto-grow="true" required>${attrValue(val('name'))}</textarea>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-6 text-sm">
                ${getNativeFieldWrapperHtml('Case File #', getReadOnlyValueHtml(sanitizeHTML(val('case_file_number'))), { className: 'md:col-span-2' })}
                ${getNativeFieldWrapperHtml('Card ID', inputHtml('text', 'card_id', val('card_id')), { className: 'md:col-span-2' })}
                ${getNativeFieldWrapperHtml('Phone', inputHtml('tel', 'phone', val('phone')), { className: 'md:col-span-4' })}
                ${getNativeFieldWrapperHtml('Email', inputHtml('email', 'email', val('email')), { className: 'md:col-span-4' })}

                ${getNativeFieldWrapperHtml('Case Type(s)', scalarControlRootHtml('types'), { className: 'md:col-span-6', required: true })}
                ${getNativeFieldWrapperHtml('Providers', scalarControlRootHtml('provider'), { className: 'md:col-span-6' })}

                ${getNativeFieldWrapperHtml('Urgency', scalarControlRootHtml('priority'), { className: 'md:col-span-3' })}
                ${getNativeFieldWrapperHtml('Status', scalarControlRootHtml('status'), { className: 'md:col-span-3' })}
                ${getNativeFieldWrapperHtml('Case Manager', scalarControlRootHtml('case_manager'), { className: 'md:col-span-3' })}
                ${getNativeFieldWrapperHtml('Next Action Due', getReadOnlyValueHtml(formatDate(nextActionDate), { id: 'next-action-date' }), { className: 'md:col-span-3' })}

                ${getNativeFieldWrapperHtml('Case Value', inputHtml('text', 'case_value', formatCurrency(val('case_value')), { className: 'currency-input', placeholder: '$', extraAttrs: 'inputmode="decimal" data-native-currency="true"' }), { className: 'md:col-span-3' })}
                ${getNativeFieldWrapperHtml('Payment Method', scalarControlRootHtml('payment_method'), { className: 'md:col-span-3' })}
                ${getNativeFieldWrapperHtml('First Seen', scalarControlRootHtml('first_seen'), { className: 'md:col-span-3' })}
                ${getNativeFieldWrapperHtml('Next Appointment', scalarControlRootHtml('next_appointment'), { className: 'md:col-span-3' })}

                ${renderLongTextDetailSection({
        fieldName: 'comments',
        label: 'Details',
        value: val('comments'),
        wrapperClass: 'md:col-span-12',
    })}
            </div>
            ${renderAuditTrail(item)}
        </div>

    `;
}

/**
 * Mounts registered type controls for Patient details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Patient item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupPatientDetailTypeControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    const root = form?.querySelector('[data-detail-scalar-control="types"]');
    if (!root) {return controls;}

    controls.types = createTypeSelectPreset({
        root,
        appData,
        itemType: 'patient',
        value: Array.isArray(item?.types) ? item.types : [],
        required: true,
        searchable: true,
    });

    return controls;
}

function getLabStatusConfig(lab) {
    return (appData?.config?.labStatus || [])
        .find((status) => status.id === lab?.status) || {};
}

function getLabPriorityConfig(lab) {
    return (appData?.config?.priorities || [])
        .find((priority) => priority.id === lab?.priority) || {};
}

function getLabTypeLabel(lab) {
    const typeIds = Array.isArray(lab?.types) ? lab.types : [];
    const requiredTypeId = typeIds[0] || '';
    const typeConfig = (appData?.config?.labTypes || [])
        .find((type) => type.id === requiredTypeId);
    return typeConfig?.name || requiredTypeId || 'Lab type missing';
}

function getLabTypeLabels(lab) {
    const typeIds = Array.isArray(lab?.types) ? lab.types : [];
    const typeConfig = appData?.config?.labTypes || [];
    return typeIds
        .map((typeId) =>
            typeConfig.find((type) => type.id === typeId)?.name || typeId)
        .filter(Boolean);
}

function formatNaturalList(values = [], fallback = '') {
    const labels = values.filter(Boolean);
    if (labels.length === 0) {return fallback;}
    if (labels.length === 1) {return labels[0];}
    if (labels.length === 2) {return `${labels[0]} and ${labels[1]}`;}
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function getLabLabel(lab) {
    const labId = lab?.lab || '';
    if (labId === 'internal') {
        return appData?.config?.labs?.internalLabLabel || 'Internal lab';
    }
    return (appData?.config?.labs?.externalLabs || [])
        .find((externalLab) => externalLab?.id === labId)?.name ||
        labId ||
        'Lab not set';
}

function getFirstNamesFromLabels(labels = []) {
    return labels
        .map((label) => String(label || '').replace(/\s+\(inactive\)$/i, '').trim())
        .filter(Boolean)
        .map((label) => label.split(/\s+/)[0])
        .filter(Boolean);
}

function getAssignedUserNames(userIds = []) {
    return (Array.isArray(userIds) ? userIds : [])
        .filter(Boolean)
        .map((userId) => getUserDisplayNameById(userId));
}

function getLatestNoteDate(note) {
    const value = note?.date;
    if (!value) {return null;}
    const date = typeof value.toDate === 'function'
        ? value.toDate()
        : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatLatestNoteDateTime(note) {
    const date = getLatestNoteDate(note);
    if (!date) {return '';}
    const parts = new Intl.DateTimeFormat('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).formatToParts(date);
    const getPart = (type) =>
        parts.find((part) => part.type === type)?.value || '';
    const dayPeriod = getPart('dayPeriod').toLowerCase();
    return `${getPart('day')}/${getPart('month')}/${getPart('year')} at ` +
        `${getPart('hour')}:${getPart('minute')} ${dayPeriod}`;
}

function getLatestLabNoteText(lab) {
    const latestNote = lab?.latestNote || {};
    const text = String(latestNote.text || '').trim();
    if (!text) {return '';}
    if (
        latestNote.isSystemNote === true &&
        /^.+ changed status from .+ to .+\.$/.test(text)
    ) {
        const dateTime = formatLatestNoteDateTime(latestNote);
        if (dateTime) {
            return `${text.replace(/\.$/, '')} on ${dateTime}`;
        }
    }
    return text;
}

function renderLatestLabNote(lab) {
    const text = getLatestLabNoteText(lab);
    if (!text) {return '';}
    const isSystemNote = lab?.latestNote?.isSystemNote === true;
    const textWithNames = convertIdsToNamesForEditing(text);
    const snippet = textWithNames.substring(0, 150) +
        (textWithNames.length > 150 ? '...' : '');
    return `
        <div class="flex items-center min-w-0 truncate text-gray-500 dark:text-gray-400" title="${attrValue(textWithNames)}">
            <span class="material-symbols-outlined text-[14px] mr-1.5">chat_bubble</span>
            <span class="text-xs truncate ${isSystemNote ? 'italic' : ''}">${sanitizeHTML(snippet)}</span>
        </div>
    `;
}

function renderLabStatus(lab) {
    const status = getLabStatusConfig(lab);
    const color = status?.color || '';
    const label = status?.name || lab?.status || 'Status not set';
    if (!color) {
        return `<span class="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">${sanitizeHTML(label)}</span>`;
    }
    const textColor = isColorDark(color) ? 'text-white' : 'text-gray-800';
    return `<span class="rounded px-2 py-0.5 text-xs font-medium ${textColor} text-center truncate" style="background-color: ${attrValue(color)};" title="Status: ${attrValue(label)}">${sanitizeHTML(label)}</span>`;
}

function renderLabMetadataValue(icon, text, title = '') {
    return `
        <span class="text-gray-500 dark:text-gray-400 flex items-center min-w-0 truncate" title="${attrValue(title || text || 'None')}">
            <span class="material-symbols-outlined text-[14px] mr-1 opacity-70 flex-shrink-0">${icon}</span>
            <span class="truncate">${sanitizeHTML(text || 'None')}</span>
        </span>
    `;
}

function getIncompleteLabChecklistItems(lab) {
    if (!lab?.checklist || typeof lab.checklist !== 'object') {return [];}
    return Object.entries(lab.checklist)
        .map(([id, data]) => ({id, ...data}))
        .filter((checklistItem) => !checklistItem.isCompleted)
        .sort((itemA, itemB) => (itemA.order || 0) - (itemB.order || 0));
}

function renderLabChecklistPreview(lab) {
    const nextItem = getIncompleteLabChecklistItems(lab)[0];
    if (!nextItem) {return '';}
    const text = nextItem.text || '';

    return `
        <div class="flex items-center min-w-0" title="Next action: ${attrValue(text)}">
            <input
                type="checkbox"
                class="kanban-checklist-toggle form-checkbox h-3.5 w-3.5 rounded border-gray-300 text-[var(--dfc-brand-primary)] focus:ring-[var(--dfc-brand-primary-ring)] cursor-pointer flex-shrink-0"
                data-lab-id="${attrValue(lab.id)}"
                data-item-id="${attrValue(nextItem.id)}"
                onclick="event.stopPropagation(); window.handleKanbanChecklistToggle(event, '${attrValue(lab.id)}', 'lab', '${attrValue(nextItem.id)}')"
            >
            <span class="ml-1.5 text-xs text-gray-500 dark:text-gray-400 truncate">${sanitizeHTML(text)}</span>
        </div>
    `;
}

function renderPatientLabRow(lab) {
    const canOpen = canAccessLabs();
    const actionAttrs = canOpen ? `data-linked-parent-section-item-id="${attrValue(lab.id)}"` : 'aria-disabled="true"';
    const actionClasses = canOpen ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : 'cursor-default';
    const assignedNames = getAssignedUserNames(lab.assigned_users || []);
    const assignedFirstNames = getFirstNamesFromLabels(assignedNames);
    const assignedText = assignedFirstNames.join(', ') || 'Unassigned';
    const assignedTitle = assignedNames.join(', ') || 'Unassigned';
    const priority = getLabPriorityConfig(lab);
    const priorityColor = priority?.color || '#d1d5db';
    const typeTitle = formatNaturalList(getLabTypeLabels(lab), 'Lab type missing');
    const labLabel = getLabLabel(lab);
    const resolvedDate = getResolvedCardDate('lab', lab, appData?.tasks || []);
    const dateText = formatDate(resolvedDate);
    const checklistHtml = renderLabChecklistPreview(lab);
    const latestNoteHtml = renderLatestLabNote(lab);
    const closedClass = isItemClosed(lab) ? 'opacity-75' : '';

    return `
        <div
            class="patient-lab-item group p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg mb-3 transition-colors shadow-sm bg-white dark:bg-gray-800 text-sm ${actionClasses} ${closedClass}"
            ${actionAttrs}
        >
            <div class="space-y-3 sm:hidden">
                <div class="flex items-start justify-between gap-3">
                    <div class="flex min-w-0 items-start">
                        <span class="mt-1.5 mr-2.5 h-2.5 w-2.5 rounded-full flex-shrink-0" style="background-color: ${attrValue(priorityColor)};" title="Urgency: ${attrValue(priority?.name || 'N/A')}"></span>
                        <span class="font-medium leading-5 text-gray-900 dark:text-gray-100 break-words min-w-0" title="${attrValue(typeTitle)}">${sanitizeHTML(typeTitle)}</span>
                    </div>
                    <div class="max-w-[45%] shrink-0">${renderLabStatus(lab)}</div>
                </div>
                <div class="space-y-2 text-xs">
                    ${renderLabMetadataValue('calendar_today', dateText || 'No date', resolvedDate || 'None')}
                    ${renderLabMetadataValue('person', assignedText, assignedTitle)}
                    ${renderLabMetadataValue('biotech', labLabel, labLabel)}
                </div>
                ${checklistHtml}
                ${latestNoteHtml}
            </div>

            <div class="hidden sm:flex sm:items-center">
                <div class="flex items-center w-[300px] flex-shrink-0 mr-4 min-w-0">
                    <span class="w-2.5 h-2.5 rounded-full mr-2.5 flex-shrink-0" style="background-color: ${attrValue(priorityColor)};" title="Urgency: ${attrValue(priority?.name || 'N/A')}"></span>
                    <span class="font-medium text-gray-900 dark:text-gray-100 truncate" title="${attrValue(typeTitle)}">${sanitizeHTML(typeTitle)}</span>
                </div>

                <div class="grid grid-cols-[100px_120px_200px_200px] gap-3 items-center flex-shrink-0 mr-4 text-xs border-r border-gray-100 dark:border-gray-700 pr-4">
                    ${renderLabMetadataValue('calendar_today', dateText || 'No date', resolvedDate || 'None')}
                    ${renderLabMetadataValue('person', assignedText, assignedTitle)}
                    ${renderLabMetadataValue('biotech', labLabel, labLabel)}
                    <div class="truncate flex items-center justify-start">${renderLabStatus(lab)}</div>
                </div>

                <div class="flex items-center flex-grow min-w-0 space-x-12">
                    ${checklistHtml ? `<div class="flex-shrink-0 min-w-0 truncate max-w-[40%]">${checklistHtml}</div>` : ''}
                    ${latestNoteHtml ? `<div class="flex-shrink min-w-0 truncate flex-grow">${latestNoteHtml}</div>` : ''}
                </div>
            </div>
        </div>
    `;
}

function getLinkedPatientLabs(patientId) {
    if (!patientId) {return [];}
    return (appData?.labs || [])
        .filter((lab) => lab?.linked_patient_id === patientId)
        .sort((labA, labB) => {
            const closedDiff = Number(isItemClosed(labA)) - Number(isItemClosed(labB));
            if (closedDiff !== 0) {return closedDiff;}

            const dateA = getResolvedCardDate('lab', labA, appData?.tasks || []);
            const dateB = getResolvedCardDate('lab', labB, appData?.tasks || []);
            if (dateA && dateB && dateA !== dateB) {
                return dateA.localeCompare(dateB);
            }
            if (dateA) {return -1;}
            if (dateB) {return 1;}
            return getLabTypeLabel(labA).localeCompare(getLabTypeLabel(labB));
        });
}

function renderAddLabAction(patient) {
    if (!canAccessLabs() || !patient?.id) {return '';}
    return `
        <button
            type="button"
            class="patient-labs-create-btn icon-action-btn"
            data-patient-add-lab
            data-patient-id="${attrValue(patient.id)}"
            title="Add Lab Item"
            aria-label="Add Lab Item"
        >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
            </svg>
        </button>
    `;
}

function getPatientLabsUiSession(patientId) {
    if (PATIENT_LABS_UI_SESSION.patientId !== patientId) {
        PATIENT_LABS_UI_SESSION.patientId = patientId;
        PATIENT_LABS_UI_SESSION.closedExpanded = false;
    }
    return PATIENT_LABS_UI_SESSION;
}

function renderPatientLabsBody(patient, openLabs, closedLabs) {
    const uiSession = getPatientLabsUiSession(patient.id);
    const closedExpanded = uiSession.closedExpanded;
    const closedSummaryClasses = closedExpanded ?
        `${CLOSED_PATIENT_LABS_SUMMARY_BUTTON_CLASSES} text-[var(--dfc-brand-accent)]` :
        `${CLOSED_PATIENT_LABS_SUMMARY_BUTTON_CLASSES} text-gray-500 hover:text-[var(--dfc-brand-primary)] dark:text-gray-400`;

    if (openLabs.length === 0 && closedLabs.length === 0) {
        return '<div class="mb-4"></div>';
    }

    return `
        <div class="mb-4">
            ${openLabs.map(renderPatientLabRow).join('')}
        </div>
        ${closedLabs.length > 0 ? `
            <button type="button" class="${closedSummaryClasses}" aria-expanded="${closedExpanded ? 'true' : 'false'}">
                <span class="material-symbols-outlined text-[18px] leading-none">${closedExpanded ? 'expand_less' : 'expand_more'}</span>
                <span>${closedLabs.length} closed lab item${closedLabs.length === 1 ? '' : 's'}</span>
            </button>
        ` : ''}
        ${closedLabs.length > 0 && closedExpanded ? `
            <div class="closed-patient-labs opacity-80">
                ${closedLabs.map(renderPatientLabRow).join('')}
            </div>
        ` : ''}
    `;
}

/**
 * Renders Patient-linked Labs as a dedicated detail section.
 * @param {object} patient Patient item data.
 * @returns {string} Labs section HTML.
 */
export function renderPatientLabsSection(patient) {
    const labs = getLinkedPatientLabs(patient?.id);
    const openLabs = labs.filter((lab) => !isItemClosed(lab));
    const closedLabs = labs.filter((lab) => isItemClosed(lab));

    return `
        <section
            class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6 mt-6"
            data-linked-parent-section="patient-labs"
        >
            <div class="mb-4 flex items-center justify-between gap-3">
                <h2 class="text-xl font-semibold dark:text-gray-100">Labs</h2>
                ${renderAddLabAction(patient)}
            </div>
            <div>
                ${renderPatientLabsBody(patient, openLabs, closedLabs)}
            </div>
        </section>
    `;
}

/**
 * Mounts registered people/provider controls for Patient details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Patient item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupPatientDetailPeopleControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    if (!form) {return controls;}

    const getRoot = (fieldName) => form.querySelector(
        `[data-detail-scalar-control="${fieldName}"]`,
    );
    const value = (fieldName, fallback = '') => item?.[fieldName] ?? fallback;

    const providerRoot = getRoot('provider');
    if (providerRoot) {
        controls.provider = createProviderSelectPreset({
            root: providerRoot,
            appData,
            name: 'provider',
            value: Array.isArray(value('provider', [])) ? value('provider', []) : [],
            searchable: true,
        });
    }

    const caseManagerRoot = getRoot('case_manager');
    if (caseManagerRoot) {
        controls.case_manager = createUserSelectPreset({
            root: caseManagerRoot,
            appData,
            name: 'case_manager',
            collectionName: 'patients',
            value: value('case_manager'),
            clearable: false,
            searchable: true,
        });
    }

    return controls;
}

/**
 * Mounts registered scalar controls for Patient details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Patient item data.
 * @returns {object} Mounted control handles keyed by field name.
 */
export function setupPatientDetailScalarControls(detailsViewContainer, item) {
    const controls = {};
    const form = detailsViewContainer?.querySelector('#details-form');
    if (!form) {return controls;}

    const getRoot = (fieldName) => form.querySelector(
        `[data-detail-scalar-control="${fieldName}"]`,
    );
    const value = (fieldName) => item?.[fieldName] ?? '';

    const priorityRoot = getRoot('priority');
    if (priorityRoot) {
        controls.priority = createPrioritySelectPreset({
            root: priorityRoot,
            appData,
            value: value('priority'),
            clearable: false,
        });
    }

    const statusRoot = getRoot('status');
    if (statusRoot) {
        controls.status = createStatusSelectPreset({
            root: statusRoot,
            appData,
            itemType: 'patient',
            value: value('status'),
            clearable: false,
        });
    }

    const paymentRoot = getRoot('payment_method');
    if (paymentRoot) {
        controls.payment_method = createSelectControl({
            root: paymentRoot,
            name: 'payment_method',
            value: value('payment_method'),
            options: getPaymentMethodOptions(value('payment_method')),
            placeholder: '+ Add Payment Method',
            clearable: true,
        });
    }

    const firstSeenRoot = getRoot('first_seen');
    if (firstSeenRoot) {
        controls.first_seen = createDateControl({
            root: firstSeenRoot,
            name: 'first_seen',
            type: 'date',
            value: value('first_seen'),
        });
    }

    const appointmentRoot = getRoot('next_appointment');
    if (appointmentRoot) {
        controls.next_appointment = createDateControl({
            root: appointmentRoot,
            name: 'next_appointment',
            type: 'datetime-local',
            value: value('next_appointment'),
        });
    }

    return controls;
}

/**
 * Attaches Patient detail actions owned by the Patient domain.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Patient item data.
 * @returns {void}
 */
export function attachPatientDetailActionListeners(detailsViewContainer, item, context = {}) {
    detailsViewContainer
        ?.querySelectorAll('[data-patient-add-lab]')
        .forEach((button) => {
            button.onclick = async () => {
                const { openLabsCreateFlow } = await import('../labs/labsFormFields.js');
                openLabsCreateFlow({
                    patientId: button.dataset.patientId || item?.id || '',
                    patientName: item?.name || '',
                });
            };
        });

    detailsViewContainer
        ?.querySelector('.closed-patient-labs-summary')
        ?.addEventListener('click', (event) => {
            event.stopPropagation();
            const uiSession = getPatientLabsUiSession(item?.id);
            uiSession.closedExpanded = !uiSession.closedExpanded;
            context.renderDetailsView?.();
        });

    detailsViewContainer
        ?.querySelectorAll('[data-linked-parent-section="patient-labs"] [data-linked-parent-section-item-id]')
        .forEach((row) => {
            row.onclick = async () => {
                if (!canAccessLabs()) {return;}
                const { showDetails } = await import('../../app/routing/navigationController.js');
                showDetails(row.dataset.linkedParentSectionItemId || '', 'lab');
            };
        });
}
